import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Grid,
  Tab,
  Tabs,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import SecurityIcon from '@mui/icons-material/Security';
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';
import SchoolIcon from '@mui/icons-material/School';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ParkIcon from '@mui/icons-material/Park';
import { useNavigate } from 'react-router-dom';

interface LoginForm {
  username: string;
  password: string;
}

interface Department {
  id: string;
  name: {
    mr: string;
    hi: string;
    en: string;
  };
  description: {
    mr: string;
    hi: string;
    en: string;
  };
  contact: string;
  email: string;
}

// Sample departments data
const departments: Department[] = [
  {
    id: 'law-order',
    name: {
      mr: 'कायदा सुव्यवस्था',
      hi: 'कानून व्यवस्था',
      en: 'Law and Order',
    },
    description: {
      mr: 'गावातील कायदा व सुव्यवस्था राखण्यासाठी समर्पित विभाग.',
      hi: 'गांव में कानून और व्यवस्था बनाए रखने के लिए समर्पित विभाग।',
      en: 'Department dedicated to maintaining law and order in the village.',
    },
    contact: '+91 1234567890',
    email: 'law@pargaon.gov.in',
  },
  {
    id: 'cultural',
    name: {
      mr: 'सांस्कृतिक',
      hi: 'सांस्कृतिक',
      en: 'Cultural',
    },
    description: {
      mr: 'सांस्कृतिक कार्यक्रम आणि परंपरा जपण्यासाठी कार्यरत विभाग.',
      hi: 'सांस्कृतिक कार्यक्रमों और परंपराओं को बढ़ावा देने वाला विभाग।',
      en: 'Department promoting cultural events and traditions.',
    },
    contact: '+91 1234567891',
    email: 'culture@pargaon.gov.in',
  },
  {
    id: 'education',
    name: {
      mr: 'शिक्षण',
      hi: 'शिक्षा',
      en: 'Education',
    },
    description: {
      mr: 'शिक्षण सुविधा आणि शैक्षणिक कार्यक्रमांचे आयोजन करणारा विभाग.',
      hi: 'शैक्षिक सुविधाओं और शैक्षणिक कार्यक्रमों का आयोजन करने वाला विभाग।',
      en: 'Department responsible for educational facilities and organizing educational programs.',
    },
    contact: '+91 1234567892',
    email: 'education@pargaon.gov.in',
  },
  {
    id: 'finance',
    name: {
      mr: 'अर्थ',
      hi: 'वित्त',
      en: 'Finance',
    },
    description: {
      mr: 'गावाच्या आर्थिक विकासासाठी आणि नियोजनासाठी कार्यरत विभाग.',
      hi: 'गांव के आर्थिक विकास और योजना के लिए कार्यरत विभाग।',
      en: 'Department responsible for the financial development and planning of the village.',
    },
    contact: '+91 1234567893',
    email: 'finance@pargaon.gov.in',
  },
  {
    id: 'environment',
    name: {
      mr: 'पर्यावरण',
      hi: 'पर्यावरण',
      en: 'Environment',
    },
    description: {
      mr: 'पर्यावरण संरक्षण आणि संवर्धनासाठी कार्यरत विभाग.',
      hi: 'पर्यावरण संरक्षण और संवर्धन के लिए कार्यरत विभाग।',
      en: 'Department responsible for environmental protection and conservation.',
    },
    contact: '+91 1234567894',
    email: 'environment@pargaon.gov.in',
  },
];

const Admin = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginForm, setLoginForm] = useState<LoginForm>({ username: '', password: '' });
  const [currentDepartment, setCurrentDepartment] = useState<Department | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [currentTab, setCurrentTab] = useState(0);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real application, you would validate credentials against a backend
    if (loginForm.username === 'admin' && loginForm.password === 'password') {
      setIsLoggedIn(true);
      setSnackbar({ open: true, message: 'Successfully logged in', severity: 'success' });
    } else {
      setSnackbar({ open: true, message: 'Invalid credentials', severity: 'error' });
    }
  };

  const handleEdit = (department: Department) => {
    setCurrentDepartment(department);
    setIsEditing(true);
  };

  const handleSave = () => {
    // In a real application, you would save changes to a backend
    setIsEditing(false);
    setSnackbar({ open: true, message: 'Changes saved successfully', severity: 'success' });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  if (!isLoggedIn) {
    return (
      <Box sx={{ py: 8, backgroundColor: 'background.default', minHeight: '100vh' }}>
        <Container maxWidth="sm">
          <Card>
            <CardContent>
              <Typography variant="h4" component="h1" gutterBottom align="center">
                Admin Login
              </Typography>
              <form onSubmit={handleLogin}>
                <TextField
                  fullWidth
                  label="Username"
                  margin="normal"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                />
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  margin="normal"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                />
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  type="submit"
                  sx={{ mt: 3 }}
                >
                  Login
                </Button>
              </form>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 6, backgroundColor: 'background.default', minHeight: '100vh' }}>
      <Container maxWidth="lg">
        <Typography variant="h4" component="h1" gutterBottom>
          Admin Dashboard
        </Typography>

        {/* Department Management */}
        <Card sx={{ mt: 4 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Manage Departments
            </Typography>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={currentTab} onChange={handleTabChange}>
                <Tab label="Department Information" />
                <Tab label="News & Updates" />
                <Tab label="Documents" />
              </Tabs>
            </Box>

            {/* Department Information Tab */}
            {currentTab === 0 && (
              <Grid container spacing={3}>
                {departments.map((dept) => (
                  <Grid item xs={12} key={dept.id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6">{dept.name.en}</Typography>
                          <IconButton onClick={() => handleEdit(dept)} color="primary">
                            <EditIcon />
                          </IconButton>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2">Contact</Typography>
                            <Typography>{dept.contact}</Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2">Email</Typography>
                            <Typography>{dept.email}</Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            {/* News & Updates Tab */}
            {currentTab === 1 && (
              <Typography>News & Updates management coming soon...</Typography>
            )}

            {/* Documents Tab */}
            {currentTab === 2 && (
              <Typography>Document management coming soon...</Typography>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={isEditing} onClose={() => setIsEditing(false)} maxWidth="md" fullWidth>
          <DialogTitle>Edit Department Information</DialogTitle>
          <DialogContent>
            {currentDepartment && (
              <Grid container spacing={3} sx={{ mt: 1 }}>
                {/* Marathi */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>मराठी</Typography>
                  <TextField
                    fullWidth
                    label="Name (Marathi)"
                    value={currentDepartment.name.mr}
                    onChange={(e) => setCurrentDepartment({
                      ...currentDepartment,
                      name: { ...currentDepartment.name, mr: e.target.value }
                    })}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Description (Marathi)"
                    value={currentDepartment.description.mr}
                    onChange={(e) => setCurrentDepartment({
                      ...currentDepartment,
                      description: { ...currentDepartment.description, mr: e.target.value }
                    })}
                    margin="normal"
                    multiline
                    rows={3}
                  />
                </Grid>

                {/* Hindi */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>हिंदी</Typography>
                  <TextField
                    fullWidth
                    label="Name (Hindi)"
                    value={currentDepartment.name.hi}
                    onChange={(e) => setCurrentDepartment({
                      ...currentDepartment,
                      name: { ...currentDepartment.name, hi: e.target.value }
                    })}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Description (Hindi)"
                    value={currentDepartment.description.hi}
                    onChange={(e) => setCurrentDepartment({
                      ...currentDepartment,
                      description: { ...currentDepartment.description, hi: e.target.value }
                    })}
                    margin="normal"
                    multiline
                    rows={3}
                  />
                </Grid>

                {/* English */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>English</Typography>
                  <TextField
                    fullWidth
                    label="Name (English)"
                    value={currentDepartment.name.en}
                    onChange={(e) => setCurrentDepartment({
                      ...currentDepartment,
                      name: { ...currentDepartment.name, en: e.target.value }
                    })}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Description (English)"
                    value={currentDepartment.description.en}
                    onChange={(e) => setCurrentDepartment({
                      ...currentDepartment,
                      description: { ...currentDepartment.description, en: e.target.value }
                    })}
                    margin="normal"
                    multiline
                    rows={3}
                  />
                </Grid>

                {/* Contact Information */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Contact Number"
                    value={currentDepartment.contact}
                    onChange={(e) => setCurrentDepartment({
                      ...currentDepartment,
                      contact: e.target.value
                    })}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={currentDepartment.email}
                    onChange={(e) => setCurrentDepartment({
                      ...currentDepartment,
                      email: e.target.value
                    })}
                    margin="normal"
                  />
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsEditing(false)}>Cancel</Button>
            <Button onClick={handleSave} startIcon={<SaveIcon />} variant="contained" color="primary">
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Admin;
